/**
 * LE RÉSULTAT — soldes intermédiaires de gestion, mois par mois (lot 101,
 * 18/09/2026).
 *
 * Cadrage « Analyse financière : 5. le résultat » et « 8. les arbitrages »,
 * maquette `maquette-analyse-resultat-v2.html`. Pur.
 *
 * LES MONTANTS SONT EN CENTIMES, SIGNÉS : + produit, − charge. Un solde
 * (marge brute, EBE, résultat net) est la somme de ses feuilles, et rien
 * d'autre : la somme de ce que la fenêtre de détail énumère est la valeur
 * affichée.
 *
 * D'OÙ VIENNENT LES CHIFFRES, et c'est le point à retenir :
 *   - VUE D'ENSEMBLE : la BALANCE DES COMPTES de Pennylane, mois par mois.
 *     Chaque compte est rattaché à une feuille du SIG par une table de
 *     correspondance MODIFIABLE (doc 5, proposition 4 ; doc 8) : les règles
 *     de l'utilisateur d'abord, puis les règles de base, le préfixe le plus
 *     long l'emportant. Un compte qui ne correspond à rien tombe dans
 *     « comptes non rattachés » — il est compté dans les soldes et montré,
 *     jamais perdu en silence.
 *   - MOIS EN COURS : la comptabilité est incomplète. Les LIGNES PRÉVUES
 *     (écran Prévisions) postérieures à aujourd'hui le complètent, rattachées
 *     par mot du libellé, et tout ce qui vient d'elles est marqué « estimé »
 *     (doc 5, décision 4).
 *   - VUE PAR ACTIVITÉ ET PAR CLIENT : Stafa, pas la comptabilité. CA des
 *     ordres et COÛT DE REVIENT RÉEL Stafa : la cascade s'arrête à la marge
 *     sur coûts directs, la structure n'est pas répartie (doc 8). Avant le
 *     début de Stafa, le coût n'existe pas : « non disponible », jamais zéro.
 *
 * LA BALANCE NE PORTE PAS LES PIÈCES : elle donne des totaux par compte. Le
 * détail d'une ligne montre donc ses COMPTES (numéro, libellé, montant), et
 * non les factures — celles-ci demanderaient une autre lecture (les écritures
 * du grand livre), qui n'est pas faite.
 */

export type Feuille =
  | 'ca_propre'
  | 'ca_affr'
  | 'achats_affr'
  | 'carburant'
  | 'peages'
  | 'loyers_vehicules'
  | 'entretien'
  | 'assurances'
  | 'autres_ext'
  | 'non_rattache'
  | 'impots_taxes'
  | 'salaires'
  | 'charges_sociales'
  | 'dotations'
  | 'financier'
  | 'exceptionnel'
  | 'is';

export const FEUILLES: readonly { readonly cle: Feuille; readonly nom: string }[] = [
  { cle: 'ca_propre', nom: 'dont transport en propre' },
  { cle: 'ca_affr', nom: 'dont affrètement' },
  { cle: 'achats_affr', nom: 'Achats d’affrètement (sous-traitance)' },
  { cle: 'carburant', nom: 'Carburant' },
  { cle: 'peages', nom: 'Péages' },
  { cle: 'loyers_vehicules', nom: 'Loyers des véhicules (location, LOA)' },
  { cle: 'entretien', nom: 'Entretien, pneus' },
  { cle: 'assurances', nom: 'Assurances' },
  { cle: 'autres_ext', nom: 'Autres charges externes' },
  { cle: 'non_rattache', nom: 'Comptes non rattachés' },
  { cle: 'impots_taxes', nom: 'Impôts et taxes' },
  { cle: 'salaires', nom: 'Salaires' },
  { cle: 'charges_sociales', nom: 'Charges sociales' },
  { cle: 'dotations', nom: 'Dotations aux amortissements' },
  { cle: 'financier', nom: 'Résultat financier (agios, intérêts)' },
  { cle: 'exceptionnel', nom: 'Résultat exceptionnel' },
  { cle: 'is', nom: 'Impôt sur les sociétés' },
];

export const EST_FEUILLE = (v: unknown): v is Feuille =>
  typeof v === 'string' && FEUILLES.some((f) => f.cle === v);

export interface LigneSig {
  readonly cle: string;
  readonly nom: string;
  /** Un solde : la somme des feuilles citées. Sinon, la feuille elle-même. */
  readonly de?: readonly Feuille[];
  readonly sous?: boolean;
  readonly fort?: boolean;
}

const JUSQUA = (fin: Feuille): Feuille[] => {
  const ordre = FEUILLES.map((f) => f.cle);
  return ordre.slice(0, ordre.indexOf(fin) + 1);
};

export const LIGNES_SIG: readonly LigneSig[] = [
  { cle: 'ca', nom: 'Chiffre d’affaires', de: ['ca_propre', 'ca_affr'], fort: true },
  { cle: 'ca_propre', nom: 'dont transport en propre', sous: true },
  { cle: 'ca_affr', nom: 'dont affrètement', sous: true },
  { cle: 'achats_affr', nom: 'Achats d’affrètement (sous-traitance)' },
  {
    cle: 'marge_brute',
    nom: 'Marge brute',
    de: ['ca_propre', 'ca_affr', 'achats_affr'],
  },
  { cle: 'carburant', nom: 'Carburant' },
  { cle: 'peages', nom: 'Péages' },
  { cle: 'loyers_vehicules', nom: 'Loyers des véhicules (location, LOA)' },
  { cle: 'entretien', nom: 'Entretien, pneus' },
  { cle: 'assurances', nom: 'Assurances' },
  { cle: 'autres_ext', nom: 'Autres charges externes' },
  { cle: 'non_rattache', nom: 'Comptes non rattachés' },
  { cle: 'va', nom: 'Valeur ajoutée', de: JUSQUA('non_rattache') },
  { cle: 'impots_taxes', nom: 'Impôts et taxes' },
  { cle: 'salaires', nom: 'Salaires' },
  { cle: 'charges_sociales', nom: 'Charges sociales' },
  {
    cle: 'ebe',
    nom: 'EBE (excédent brut d’exploitation)',
    de: JUSQUA('charges_sociales'),
    fort: true,
  },
  { cle: 'dotations', nom: 'Dotations aux amortissements' },
  { cle: 'rex', nom: 'Résultat d’exploitation', de: JUSQUA('dotations') },
  { cle: 'financier', nom: 'Résultat financier (agios, intérêts)' },
  { cle: 'exceptionnel', nom: 'Résultat exceptionnel' },
  { cle: 'is', nom: 'Impôt sur les sociétés' },
  { cle: 'rn', nom: 'Résultat net', de: JUSQUA('is'), fort: true },
];

/**
 * LES RÈGLES DE BASE, sur le plan comptable général. Elles s'arrêtent où le
 * plan comptable cesse d'être sans ambiguïté : le carburant se tient selon
 * les entreprises en 6022 ou en 6061, les péages et les loyers de véhicules
 * n'ont pas de compte à eux. Le reste est laissé NON RATTACHÉ, montré à
 * l'écran, et se règle par une règle de l'utilisateur — mieux qu'un compte
 * rangé au hasard dans une ligne du SIG.
 *
 * SUPPOSÉ, donc, pour `6022` et `6061` : à confirmer sur la balance réelle.
 */
export const CORRESPONDANCES_DE_BASE: readonly {
  readonly prefixe: string;
  readonly feuille: Feuille;
}[] = [
  { prefixe: '70', feuille: 'ca_propre' },
  { prefixe: '611', feuille: 'achats_affr' },
  { prefixe: '6022', feuille: 'carburant' },
  { prefixe: '6061', feuille: 'carburant' },
  { prefixe: '615', feuille: 'entretien' },
  { prefixe: '616', feuille: 'assurances' },
  { prefixe: '62', feuille: 'autres_ext' },
  { prefixe: '63', feuille: 'impots_taxes' },
  { prefixe: '641', feuille: 'salaires' },
  { prefixe: '644', feuille: 'salaires' },
  { prefixe: '645', feuille: 'charges_sociales' },
  { prefixe: '646', feuille: 'charges_sociales' },
  { prefixe: '647', feuille: 'charges_sociales' },
  { prefixe: '681', feuille: 'dotations' },
  { prefixe: '66', feuille: 'financier' },
  { prefixe: '76', feuille: 'financier' },
  { prefixe: '67', feuille: 'exceptionnel' },
  { prefixe: '77', feuille: 'exceptionnel' },
  { prefixe: '695', feuille: 'is' },
  { prefixe: '699', feuille: 'is' },
];

export interface Correspondance {
  readonly id: string;
  /** Début du numéro de compte (« 6022 »), ou mot du libellé d'une prévision. */
  readonly motif: string;
  readonly genre: 'compte' | 'libelle';
  readonly feuille: Feuille;
}

/** Rattachement d'un compte : règle de l'utilisateur d'abord, préfixe le plus long. */
export function feuilleDuCompte(
  numero: string,
  regles: readonly Correspondance[],
): { feuille: Feuille; regle: string | null } {
  const siennes = regles
    .filter((r) => r.genre === 'compte' && numero.startsWith(r.motif))
    .sort((a, b) => b.motif.length - a.motif.length)[0];
  if (siennes !== undefined) return { feuille: siennes.feuille, regle: siennes.motif };
  const base = CORRESPONDANCES_DE_BASE.filter((r) => numero.startsWith(r.prefixe)).sort(
    (a, b) => b.prefixe.length - a.prefixe.length,
  )[0];
  if (base !== undefined) return { feuille: base.feuille, regle: base.prefixe };
  return { feuille: 'non_rattache', regle: null };
}

/**
 * LES MOTS DE BASE pour rattacher une ligne prévue (elle n'a pas de compte).
 * Mêmes mots que le rangement de la trésorerie, sans les inventer deux fois.
 */
export const MOTS_DE_BASE: readonly {
  readonly motif: string;
  readonly feuille: Feuille;
}[] = [
  { motif: 'GASOIL', feuille: 'carburant' },
  { motif: 'GAZOLE', feuille: 'carburant' },
  { motif: 'CARBURANT', feuille: 'carburant' },
  { motif: 'PEAGE', feuille: 'peages' },
  { motif: 'TELEPEAGE', feuille: 'peages' },
  { motif: 'LOA', feuille: 'loyers_vehicules' },
  { motif: 'LOCATION', feuille: 'loyers_vehicules' },
  { motif: 'ENTRETIEN', feuille: 'entretien' },
  { motif: 'GARAGE', feuille: 'entretien' },
  { motif: 'PNEU', feuille: 'entretien' },
  { motif: 'PNEUS', feuille: 'entretien' },
  { motif: 'ASSURANCE', feuille: 'assurances' },
  { motif: 'SALAIRE', feuille: 'salaires' },
  { motif: 'SALAIRES', feuille: 'salaires' },
  { motif: 'URSSAF', feuille: 'charges_sociales' },
  { motif: 'RETRAITE', feuille: 'charges_sociales' },
  { motif: 'MUTUELLE', feuille: 'charges_sociales' },
  { motif: 'TAXE', feuille: 'impots_taxes' },
  { motif: 'IMPOT', feuille: 'impots_taxes' },
  { motif: 'AFFRETEMENT', feuille: 'achats_affr' },
  { motif: 'SOUS TRAITANCE', feuille: 'achats_affr' },
];

/** Majuscules sans accent, ponctuation en espaces — comme la trésorerie. */
export function motsDe(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

export function feuilleDuLibelle(
  texte: string,
  regles: readonly Correspondance[],
  parDefaut: Feuille,
): { feuille: Feuille; regle: string | null } {
  const mots = ` ${motsDe(texte)} `;
  for (const r of regles.filter((x) => x.genre === 'libelle')) {
    if (mots.includes(` ${motsDe(r.motif)} `))
      return { feuille: r.feuille, regle: r.motif };
  }
  for (const r of MOTS_DE_BASE) {
    if (mots.includes(` ${r.motif} `)) return { feuille: r.feuille, regle: r.motif };
  }
  return { feuille: parDefaut, regle: null };
}

// ---------------------------------------------------------------------------
// Le SIG d'un mois
// ---------------------------------------------------------------------------

/** Un compte de la balance, en centimes. */
export interface CompteBalance {
  readonly numero: string;
  readonly libelle: string;
  readonly debits: number;
  readonly credits: number;
}

/** Une ligne prévue, ramenée au mois. */
export interface PrevueSig {
  readonly cle: string;
  readonly libelle: string;
  readonly date: string;
  /** HT signé : + produit, − charge. */
  readonly ht: number;
  readonly cote: 'client' | 'fournisseur';
}

export interface ApportSig {
  readonly cle: string;
  readonly libelle: string;
  readonly montant: number;
  readonly note: string;
  readonly estime: boolean;
}

export interface MoisSig {
  readonly mois: string;
  readonly valeurs: Readonly<Record<Feuille, number>>;
  readonly apports: Readonly<Record<Feuille, readonly ApportSig[]>>;
  /** Vrai si une part vient des prévisions. */
  readonly estime: boolean;
  /** Vrai si la balance du mois n'a pas été lue. */
  readonly sansBalance: boolean;
}

const vide = (): Record<Feuille, number> =>
  Object.fromEntries(FEUILLES.map((f) => [f.cle, 0])) as Record<Feuille, number>;

const videApports = (): Record<Feuille, ApportSig[]> =>
  Object.fromEntries(FEUILLES.map((f) => [f.cle, [] as ApportSig[]])) as Record<
    Feuille,
    ApportSig[]
  >;

export function sigDuMois(
  mois: string,
  comptes: readonly CompteBalance[],
  prevues: readonly PrevueSig[],
  regles: readonly Correspondance[],
): MoisSig {
  const valeurs = vide();
  const apports = videApports();
  for (const c of comptes) {
    // + produit, − charge : le crédit moins le débit, pour tous les comptes.
    const montant = c.credits - c.debits;
    if (montant === 0) continue;
    const { feuille, regle } = feuilleDuCompte(c.numero, regles);
    valeurs[feuille] += montant;
    apports[feuille].push({
      cle: `c:${c.numero}`,
      libelle: `${c.numero} ${c.libelle}`,
      montant,
      note: regle === null ? 'compte non rattaché' : `règle « ${regle} »`,
      estime: false,
    });
  }
  for (const p of prevues) {
    const { feuille, regle } = feuilleDuLibelle(
      p.libelle,
      regles,
      p.cote === 'client' ? 'ca_propre' : 'autres_ext',
    );
    valeurs[feuille] += p.ht;
    apports[feuille].push({
      cle: `p:${p.cle}`,
      libelle: p.libelle,
      montant: p.ht,
      note: `prévision du ${p.date}${regle === null ? '' : `, mot « ${regle} »`}`,
      estime: true,
    });
  }
  return {
    mois,
    valeurs,
    apports,
    estime: prevues.length > 0,
    sansBalance: comptes.length === 0,
  };
}

/** La valeur d'une ligne du SIG pour un mois. */
export function valeurLigne(ligne: LigneSig, m: MoisSig): number {
  if (ligne.de === undefined) return m.valeurs[ligne.cle as Feuille];
  return ligne.de.reduce((s, f) => s + m.valeurs[f], 0);
}

/** Les feuilles d'une ligne, pour la fenêtre de détail. */
export function feuillesDeLigne(ligne: LigneSig): readonly Feuille[] {
  return ligne.de ?? [ligne.cle as Feuille];
}

export function cumul(mois: readonly MoisSig[]): MoisSig {
  const valeurs = vide();
  const apports = videApports();
  for (const m of mois) {
    for (const f of FEUILLES) {
      valeurs[f.cle] += m.valeurs[f.cle];
      apports[f.cle].push(...m.apports[f.cle]);
    }
  }
  return {
    mois: 'cumul',
    valeurs,
    apports,
    estime: mois.some((m) => m.estime),
    sansBalance: mois.every((m) => m.sansBalance),
  };
}

// ---------------------------------------------------------------------------
// Les comparaisons (doc 8 : mois précédent ET moyenne des trois précédents)
// ---------------------------------------------------------------------------

export interface Comparaison {
  readonly valeur: number;
  readonly objectif: number | null;
  readonly precedent: number | null;
  readonly moyenne3: number | null;
}

export function comparer(
  mois: readonly MoisSig[],
  rang: number,
  ligne: LigneSig,
  objectif: number | null,
): Comparaison {
  const m = mois[rang];
  const trois = mois.slice(Math.max(0, rang - 3), rang);
  return {
    valeur: m === undefined ? 0 : valeurLigne(ligne, m),
    objectif,
    precedent: rang > 0 ? valeurLigne(ligne, mois[rang - 1] as MoisSig) : null,
    moyenne3:
      trois.length === 0
        ? null
        : Math.round(
            trois.reduce((s, x) => s + valeurLigne(ligne, x), 0) / trois.length,
          ),
  };
}

// ---------------------------------------------------------------------------
// Les vues par activité et par client — Stafa, marge sur coûts directs
// ---------------------------------------------------------------------------

/** Un ordre de transport ramené au mois de sa livraison. */
export interface OrdreSig {
  readonly id: string;
  readonly numero: string;
  readonly mois: string;
  readonly activite: string | null;
  readonly client: string;
  readonly client_nom: string;
  readonly vente_ht: number;
  /** Coût de revient réel figé, ou nul : avant Stafa, il n'existe pas. */
  readonly cout_reel: number | null;
  readonly achat_ht: number | null;
}

/**
 * Une charge directe rattachée à une activité (fournisseur rangé sur un
 * centre de profit). `ht` est un COÛT, POSITIF — contrairement aux feuilles du
 * SIG, qui sont signées.
 */
export interface ChargeSig {
  readonly cle: string;
  readonly libelle: string;
  readonly mois: string;
  readonly activite: string | null;
  readonly ht: number;
}

export interface MargeDirecte {
  readonly mois: string;
  readonly ca: number;
  readonly achats_affr: number;
  readonly couts: number;
  readonly marge: number | null;
  readonly taux: number | null;
  readonly ordres: readonly OrdreSig[];
  readonly charges: readonly ChargeSig[];
  /** Vrai quand un ordre du mois n'a pas de coût réel (avant Stafa). */
  readonly non_disponible: boolean;
}

export type VueDirecte =
  | { readonly sorte: 'activite'; readonly cle: string }
  | { readonly sorte: 'client'; readonly cle: string };

/**
 * LA MARGE SUR COÛTS DIRECTS d'un mois, pour une activité ou un client.
 *
 * Par CLIENT : CA de ses ordres − leur coût de revient réel Stafa (doc 8).
 * Par ACTIVITÉ : même chose, plus les charges des fournisseurs rattachés à
 * l'activité. La structure n'est jamais répartie.
 */
export function margeDirecte(
  mois: string,
  vue: VueDirecte,
  ordres: readonly OrdreSig[],
  charges: readonly ChargeSig[],
): MargeDirecte {
  const miens = ordres.filter(
    (o) =>
      o.mois === mois &&
      (vue.sorte === 'activite' ? o.activite === vue.cle : o.client === vue.cle),
  );
  const siennes =
    vue.sorte === 'activite'
      ? charges.filter((c) => c.mois === mois && c.activite === vue.cle)
      : [];
  const ca = miens.reduce((s, o) => s + o.vente_ht, 0);
  const achats = miens.reduce((s, o) => s + (o.achat_ht ?? 0), 0);
  const sansCout = miens.some((o) => o.cout_reel === null);
  const couts =
    miens.reduce((s, o) => s + (o.cout_reel ?? 0), 0) +
    siennes.reduce((s, c) => s + c.ht, 0);
  const marge = sansCout ? null : ca - couts;
  return {
    mois,
    ca,
    achats_affr: achats,
    couts,
    marge,
    taux: marge === null || ca === 0 ? null : marge / ca,
    ordres: miens,
    charges: siennes,
    non_disponible: sansCout,
  };
}

/** Les douze mois glissants qui finissent au mois donné, du plus ancien au dernier. */
export function moisGlissants(dernier: string, nombre = 12): string[] {
  const a = Number(dernier.slice(0, 4));
  const m = Number(dernier.slice(5, 7));
  const res: string[] = [];
  for (let k = nombre - 1; k >= 0; k -= 1) {
    const d = new Date(Date.UTC(a, m - 1 - k, 1));
    res.push(d.toISOString().slice(0, 7));
  }
  return res;
}

/**
 * CONTRÔLE repris de la maquette : le résultat net est la somme de TOUTES les
 * feuilles, et chaque solde la somme des siennes. Rend les écarts.
 */
export function verifierSig(m: MoisSig): string[] {
  const fautes: string[] = [];
  for (const l of LIGNES_SIG) {
    if (l.de === undefined) continue;
    const somme = l.de.reduce((s, f) => s + m.valeurs[f], 0);
    if (valeurLigne(l, m) !== somme)
      fautes.push(`${l.cle} : solde ≠ somme des feuilles`);
  }
  for (const f of FEUILLES) {
    const somme = m.apports[f.cle].reduce((s, a) => s + a.montant, 0);
    if (somme !== m.valeurs[f.cle]) fautes.push(`${f.cle} : apports ≠ valeur`);
  }
  return fautes;
}
